import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./Styles/JobsPost.css";
import Check from "../../../asset/svgs/Check";
import Briefcase from "../../../asset/svgs/Briefcase";
import Task from "../../../asset/svgs/Task";
import Eye from "../../../asset/svgs/Eye";
import Delete from "../../../asset/svgs/Delete";
import { message, Modal, Pagination, Spin, Tag } from "antd";
import Users from "../../../asset/svgs/Users";
import DynamicBreadcrumb from "../Components/Breadcrumbs";
import JobTitle from "../Components/JobTitle";
import { useDeleteJobPost, useGetJobPosts } from "../hooks/useJobs";
import moment from "moment";
import EmptyJobs from "../Components/EmptyJobs";
import { NeonHexagonLoader } from "../Components/JobLoader";
import { getUserId } from "../helper";
import { categoryMap, jobTypeLabels } from "../utils/jobMapping";

const JobsPosts = () => {
  const [activeTab, setActiveTab] = useState("All Jobs");
  const [currentPage, setCurrentPage] = useState(1);
  const navigate = useNavigate();
  const { getJobPosts, loading: jobLoading, data: jobPosts } = useGetJobPosts();
  const { deleteJobPost, error } = useDeleteJobPost();
  const [deletingJobId, setDeletingJobId] = useState(null);
  const userId = getUserId();
  const jobsPerPage = 6;

  useEffect(() => {
    getJobPosts({
      user_id: userId,
      page: 1,
      category: "",
      job_type: "",
      salary_date: "",
    });
  }, [getJobPosts, userId]);

  const hasExpired = (expiration_date) =>
    moment(expiration_date).isBefore(moment(), "day");

  const totalApplicants = jobPosts?.reduce(
    (acc, job) => acc + job.applications,
    0
  );
  const totalJobPost = jobPosts?.length;

  const validJobs = jobPosts?.filter((job) => !hasExpired(job.expiration_date));
  const expiredJobs = jobPosts?.filter((job) =>
    hasExpired(job.expiration_date)
  );

  const jobsToDisplay = activeTab === "All Jobs" ? validJobs : expiredJobs;

  const handlePageChange = (page) => setCurrentPage(page);

  const currentJobs = jobsToDisplay?.slice(
    (currentPage - 1) * jobsPerPage,
    currentPage * jobsPerPage
  );

  const handleViewJobDetails = (jobId) =>
    navigate(`/jobs/job-admin/job-posts/job-created/${jobId}`);

  const handleDeleteJob = (jobId) => {
    Modal.confirm({
      title: "Delete this job?",
      content: "This action cannot be undone.",
      okText: "Yes, delete",
      okType: "danger",
      cancelText: "No, keep it",
      onOk: async () => {
        setDeletingJobId(jobId);
        try {
          await deleteJobPost({ user_id: userId, id: jobId });
          message.success("Job deleted successfully");
          getJobPosts({
            user_id: userId,
            page: 1,
            category: "",
            job_type: "",
            salary_date: "",
          });
        } catch (err) {
          message.error("Failed to delete job. Please try again.");
        } finally {
          setDeletingJobId(null);
        }
      },
    });
  };

  if (jobLoading)
    return (
      <div
        className="job-loader-wrapper"
        style={{
          width: "100%",
          height: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <NeonHexagonLoader />
      </div>
    );

  if (error) return message.error(error);

  return (
    <div className="job-posts-container">
      {/* Header */}
      <header className="job-posts-header">
        <h1 className="header-title">Jobs Created</h1>
        <p className="header-subtitle">
          Create a new job or edit/view existing jobs
        </p>
      </header>

      {/* Actions */}
      <div className="job-posts-actions">
        <DynamicBreadcrumb />
        <button
          className="create-job-btn"
          onClick={() => navigate("/jobs/job-admin/job-posts/create-job")}
        >
          <Task />
          <span>Create New Job</span>
        </button>
      </div>

      {/* Stats */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-content">
            <div className="stat-icon-container green">
              <Briefcase />
            </div>
            <div className="stat-info">
              <p className="stat-label">Jobs Created</p>
              <p className="stat-value">{totalJobPost || 0}</p>
            </div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-content">
            <div className="stat-icon-container green">
              <Check />
            </div>
            <div className="stat-info">
              <p className="stat-label">Number of Applicants</p>
              <p className="stat-value">{totalApplicants || 0}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="job-posts-tabs">
        {["All Jobs", "Jobs History"].map((tab) => (
          <button
            key={tab}
            className={`job-tab ${activeTab === tab ? "active" : ""}`}
            onClick={() => setActiveTab(tab)}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Jobs Grid */}
      <div className="job-cards-grid">
        {currentJobs?.length ? (
          currentJobs.map((job) => (
            <div key={job.id} className="job-card">
              <JobTitle jobData={job} />

              <p className="job-applicants">
                <Users /> {job.applications} Applicants Applied
              </p>

              <div className="job-post-tags">
                <Tag>{job.location}</Tag>
                <Tag>{jobTypeLabels[job.job_type] || job.job_type}</Tag>
                <Tag>
                  {job.category === "1" && job.custom_category
                    ? job.custom_category
                    : categoryMap[job.category] || "Not specified"}
                </Tag>
              </div>

              <div className="job-actions">
                <button
                  className="job-btn"
                  onClick={() => handleViewJobDetails(job.id)}
                >
                  <Eye /> View Applicants
                </button>
                <button
                  className="job-btn"
                  title="Delete Job"
                  onClick={() => handleDeleteJob(job.id)}
                >
                  {deletingJobId === job.id ? (
                    <Spin size="small" />
                  ) : (
                    <Delete size={16} />
                  )}
                  {deletingJobId === job.id ? "Deleting..." : "Delete"}
                </button>
              </div>
            </div>
          ))
        ) : (
          <EmptyJobs />
        )}
      </div>

      {/* Pagination */}
      {jobsToDisplay?.length > jobsPerPage && (
        <Pagination
          className="jobs-pagination"
          current={currentPage}
          pageSize={jobsPerPage}
          total={jobsToDisplay.length}
          onChange={handlePageChange}
          showSizeChanger={false}
          showQuickJumper
        />
      )}
    </div>
  );
};

export default JobsPosts;
